import { inject, Injectable } from '@angular/core';
import { Observable, of } from "rxjs";
import { ApiService } from "@core/services/api.service";
import { AppConstants } from "@core/constants";
import { catchError } from "rxjs/operators";
import { CustomRendererService } from "@shared/services/custom-renderer.service";
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TEST_EQUIPMENT_DEVICE_LIST } from "@shared/enums/test-equipment-device-list.enum";
import { HelperService } from "@shared/services/helper.service";
import { TestEqupimentListParams } from '@shared/interfaces/test-equipment-list-params.interface';

@Injectable()
export class TestEquipmentService {
  private apiService: ApiService = inject(ApiService);
  private helperService: HelperService = inject(HelperService);
  private customRendererService: CustomRendererService = inject(CustomRendererService);
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private allTestEquipmentTypeList: TEST_EQUIPMENT_DEVICE_LIST[] = [
    TEST_EQUIPMENT_DEVICE_LIST.GPS_DEVICE,
    TEST_EQUIPMENT_DEVICE_LIST.POWER_METER,
    TEST_EQUIPMENT_DEVICE_LIST.LACTATE_METER,
    TEST_EQUIPMENT_DEVICE_LIST.VCO2_VO2_ANALYZER,
  ];

  public filteredDeviceList: any[] = [];

  public getTestEquipmentList(listParams?: TestEqupimentListParams): Observable<any[]> {
    const urlParams: Record<string, any> = {};

    if (listParams?.source) {
      urlParams.source = listParams.source;
    }

    if (listParams?.sport_slug) {
      urlParams.sport_slug = listParams.sport_slug;
    }

    if (listParams?.athlete) {
      urlParams.athlete = listParams.athlete;
    }

    if (listParams?.test) {
      urlParams.test = listParams.test;
    }

    if (!Object.keys(urlParams).length) {
      return this.apiService.get(AppConstants.API.TEST_EQUIPMENT.GET);
    }

    const params: URLSearchParams = new URLSearchParams(urlParams);
    return this.apiService.get(`${AppConstants.API.TEST_EQUIPMENT.GET}?${params.toString()}`);
  }

  public save(payload: any): Observable<any> {
    return this.apiService.post(AppConstants.API.TEST_EQUIPMENT.POST, payload)
      .pipe(
        catchError(error => {
            this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
            this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
            return of(null);
          }
        )
      )
  }

  public getDeviceListByCategoryName(deviceList: any[], categoryList: TEST_EQUIPMENT_DEVICE_LIST[], selectedEquipment?: number[], organizationId?: number): any[] {
    const resultDeviceList: any[] = [];

    let deviceListCategories = Array.from(new Set(deviceList.map(device => {
      if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VO2_ANALYZER) {
        return TEST_EQUIPMENT_DEVICE_LIST.VCO2_VO2_ANALYZER;
      }
      return device.category;
    })));
    if (categoryList.length) {
      deviceListCategories = categoryList;
    }

    deviceListCategories.forEach((category: string) => {
      let selectedDevice;
      let athleteDefault;
      let organizationDefault;
      const tempDevice = deviceList.find((device: any) => device.temp_category === category);
      const comboBoxDeviceList = deviceList.filter((device: any) => device.temp_category === category);
      comboBoxDeviceList.forEach(device => {
        if ((selectedEquipment ?? []).includes(device.id)) {
          selectedDevice = device.id;  // Should only match one per category
          athleteDefault = device.id;
        }
        if (organizationId != null && (device.organizations ?? []).includes(organizationId)) {
          organizationDefault = device.id;
        }
      });
      if (tempDevice != null) {
        const tempObject: any = {
          deviceName: tempDevice.category_name,
          deviceCategory: tempDevice.temp_category,
          comboBoxDeviceList: comboBoxDeviceList,
        };
        if (selectedDevice != null) {
          tempObject.selectedDevice = selectedDevice;
        }
        if (athleteDefault != null) {
          tempObject.athleteDefault = athleteDefault;
        }
        if (organizationDefault != null) {
          tempObject.organizationDefault = organizationDefault;
        }
        resultDeviceList.push(tempObject);
      }
    });

    return resultDeviceList;
  }

  public getFilteredDeviceList(deviceList: any[]): any[] {
    const copyDeviceList: any[] = this.helperService.makeCopy(deviceList);

    copyDeviceList.forEach((device: any) => {
      if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VO2_ANALYZER) {
        device.category_name = 'VO2 Analyzer / Metabolic Cart';
        device.temp_category = TEST_EQUIPMENT_DEVICE_LIST.VCO2_VO2_ANALYZER;
      } else {
        device.temp_category = device.category;
      }
    });

    return copyDeviceList.filter((device: any) => {
      if (device.category !== TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER) {
        return device;
      }
    });
  }

  public generateEquipmentList(originalDeviceList: any[], selectedEquipment?: number[]): any {
    this.filteredDeviceList = this.getFilteredDeviceList(originalDeviceList);
    this.filteredDeviceList.forEach((device: any) => {
      device.is_user_default = false;
    });
    return this.getDeviceListByCategoryName(this.filteredDeviceList, [], selectedEquipment);
  }

  public generateEquipmentListWithDefaults(originalDeviceList: any[], selectedEquipment?: number[], organizationId?: number): any {
    this.filteredDeviceList = this.getFilteredDeviceList(originalDeviceList);
    return this.getDeviceListByCategoryName(this.filteredDeviceList, [], selectedEquipment, organizationId);
  }

  public generateEquipmentPayload(device: any, payload: any, athleteId?: number): any {
    const copyPayload: any = this.helperService.makeCopy(payload);

    let tempObject: any = {
      id: device.data.id,
      name: device.name,
      category: device.category,
      changes: {
        selected_error_function: device.data.selected_error_function,
      },
    };

    let tempObject2: any = undefined;
    if (device.data.id2) {
      tempObject2 = {
        id: device.data.id2,
        name: device.name,
        category: TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER,  // Only device that can be a '2' currently
        changes: {
          selected_error_function: device.data.selected_error_function2,
        },
      };
    }

    if (device.data?.organization_default) {
      tempObject["organization_default"] = true;
      if (tempObject2 != null) {
        tempObject2["organization_default"] = true;
      }
    }
    if (device.data?.athlete_default) {
      tempObject["athlete"] = athleteId;
      if (tempObject2 != null) {
        tempObject2["athlete"] = athleteId;
      }
    }

    if (device.data.hasOwnProperty('selected_error_function2') && device.data.selected_error_function2) {
      tempObject2.changes.selected_error_function = device.data.selected_error_function2;
    }

    if (device.data.a_slope !== '' && device.data.a_slope !== undefined && device.data.b_offset !== '' && device.data.b_offset !== undefined) {
      tempObject.changes.linear_function = {
        a: device.data.a_slope,
        b: device.data.b_offset,
      }
    }

    if (device.data.a_slope2 !== '' && device.data.a_slope2 !== undefined && device.data.b_offset2 !== '' && device.data.b_offset2 !== undefined) {
      tempObject2.changes.linear_function = {
        a: device.data.a_slope2,
        b: device.data.b_offset2,
      }
    }

    if (device.data.a !== '' && device.data.a !== undefined && device.data.b !== '' && device.data.b !== undefined && device.data.c !== '' && device.data.c !== undefined && device.data.d !== '' && device.data.d !== undefined) {
      tempObject.changes.exponential_function = {
        a: device.data.a,
        b: device.data.b,
        c: device.data.c,
        d: device.data.d,
      }
    }

    if (device.data.a2 !== '' && device.data.a2 !== undefined && device.data.b2 !== '' && device.data.b2 !== undefined && device.data.c2 !== '' && device.data.c2 !== undefined && device.data.d !== '' && device.data.d2 !== undefined) {
      tempObject2.changes.exponential_function = {
        a: device.data.a2,
        b: device.data.b2,
        c: device.data.c2,
        d: device.data.d2,
      }
    }

    if (device.data.revertToDefault) {
      tempObject["id"] = device.data.revertToDefault;
      tempObject["changes"] = {}
      tempObject2 = undefined;
    }

    const match: any = copyPayload.equipment.find((equipment: any) => equipment.category === tempObject.category);

    if (!match) {
      copyPayload.equipment.push(tempObject);
    } else {
      copyPayload.equipment.forEach((equipment: any) => {
        if (equipment.category === tempObject.category) {
          equipment.id = tempObject.id;
          equipment.changes = tempObject.changes;
          equipment.name = tempObject.name;
          if (tempObject.organization_default) {
            equipment.organization_default = tempObject.organization_default;
          }
          if (tempObject.athlete != null) {
            equipment.athlete = tempObject.athlete;
          }
        }
      });
    }

    if (tempObject2 != null) {
      const match2: any = copyPayload.equipment.find((equipment: any) => equipment.category === tempObject2.category);

      if (!match2) {
        copyPayload.equipment.push(tempObject2);
      } else {
        copyPayload.equipment.forEach((equipment: any) => {
          if (equipment.category === tempObject2.category) {
            equipment.id = tempObject2.id;
            equipment.changes = tempObject2.changes;
            equipment.name = tempObject2.name;
            if (tempObject2.organization_default) {
              equipment.organization_default = tempObject2.organization_default;
            }
            if (tempObject2.athlete != null) {
              equipment.athlete = tempObject2.athlete;
            }
          }
        });
      }
    }

    return copyPayload;
  }

  public prepareEquipmentDevicePayload(payload: any, deviceList: any[]): any {
    let additionalDeviceList: any[] = [];
    let device: any = {};
    let copyPayload: any = this.helperService.makeCopy(payload);

    copyPayload.equipment.forEach((equipment: any) => {
      delete equipment.category;


      if (equipment.changes.hasOwnProperty('linear_function2')) {
        const secondObject: any = deviceList.find((device: any) => device.name === equipment.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);

        device = {
          id: secondObject.id ? secondObject.id : equipment.id,
          organization: equipment.organization,
          changes: {
            linear_function: {
              a: equipment.changes.linear_function2.a2,
              b: equipment.changes.linear_function2.b2,
            },
            selected_error_function: equipment.changes.selected_error_function2,

          }
        };
        additionalDeviceList.push(device);

        if (equipment.hasOwnProperty('name') && equipment.name) {
          delete equipment.name;
        }
        delete equipment.changes.linear_function2;
        delete equipment.changes.selected_error_function2;
      } else if (equipment.changes.hasOwnProperty('exponential_function2')) {
        device.changes = {
          ...device.changes,
          exponential_function: {
            a: equipment.changes.exponential_function2.a2,
            b: equipment.changes.exponential_function2.b2,
            c: equipment.changes.exponential_function2.c2,
            d: equipment.changes.exponential_function2.d2,
          },
          selected_error_function: equipment.changes.selected_error_function2,
        };

        delete equipment.changes.exponential_function2;
        delete equipment.changes.selected_error_function2;
      }
    });

    copyPayload.equipment = [...copyPayload.equipment, ...additionalDeviceList];

    return copyPayload;
  }
}
